import { Badge } from '~/components/ui/badge'
import { CustomSelect } from '~/components/ui/custom-select'
import type { MappingProposal, MonitoringIndicator } from '~/api/types'

/** 매핑 테이블 한 행의 소스 키 — 키 이름, 샘플 표시 문자열, 수치 여부. */
export interface MappingTableKey {
    key: string
    sampleText: string
    numeric?: boolean
}

export interface MappingTableProps {
    /** 소스 키 목록(CSV 컬럼 / API 프로파일). */
    keys: MappingTableKey[]
    /** 소스 키 → 지표 매핑. */
    mapping: Record<string, string>
    /** 소스 키 → AI/규칙 제안. */
    proposals: Record<string, MappingProposal>
    onMap: (key: string, fieldId: string) => void
    indicators: MonitoringIndicator[]
    /** 첫 열 헤더 라벨(CSV: '소스 컬럼', API: '소스 키'). */
    keyHeader: string
    /** 측정 시각 키 선택값/핸들러. */
    measuredAtKey: string
    onMeasuredAtKey: (value: string) => void
    /** 측정 시각 키 라벨(CSV: '측정 시각 컬럼', API: '측정 시각 키'). */
    measuredAtLabel: string
    /** 측정 시각 CustomSelect 옵션(소스별로 구성이 달라 호출부에서 전달). */
    measuredAtOptions: { value: string; label: string }[]
    /** 측정 시각 CustomSelect 검색 가능 여부(API만 true). */
    measuredAtSearchable?: boolean
}

/** 소스 키 → 지표 매핑 테이블 + 측정 시각 키 선택. CSV/API 패널이 공유한다. */
export function MappingTable({
    keys,
    mapping,
    proposals,
    onMap,
    indicators,
    keyHeader,
    measuredAtKey,
    onMeasuredAtKey,
    measuredAtLabel,
    measuredAtOptions,
    measuredAtSearchable = false,
}: MappingTableProps) {
    return (
        <>
            <div className='mb-3'>
                <label className='text-muted-foreground text-xs'>
                    {measuredAtLabel}
                </label>
                <CustomSelect
                    ariaLabel={measuredAtLabel}
                    value={measuredAtKey}
                    onValueChange={onMeasuredAtKey}
                    containerClassName='ml-2 inline-block w-auto align-middle'
                    className='border-border h-8'
                    searchable={measuredAtSearchable}
                    searchPlaceholder='키 검색…'
                    options={measuredAtOptions}
                />
            </div>

            <table className='w-full text-sm'>
                <thead>
                    <tr className='text-muted-foreground border-border border-b text-xs'>
                        <th className='py-2 pr-3 text-left'>{keyHeader}</th>
                        <th className='py-2 pr-3 text-left'>샘플</th>
                        <th className='py-2 text-left'>매핑 지표</th>
                    </tr>
                </thead>
                <tbody>
                    {keys.map((item) => {
                        const p = proposals[item.key]
                        return (
                            <tr
                                key={item.key}
                                className='border-border border-b align-top last:border-0'>
                                <td className='text-foreground py-2 pr-3 font-medium'>
                                    {item.key}
                                    {item.numeric ? (
                                        <span className='text-muted-foreground ml-1 text-xs'>
                                            (수치)
                                        </span>
                                    ) : null}
                                </td>
                                <td className='text-muted-foreground py-2 pr-3 text-xs'>
                                    {item.sampleText}
                                </td>
                                <td className='py-2'>
                                    <CustomSelect
                                        ariaLabel={`${item.key} 매핑 지표`}
                                        value={mapping[item.key] ?? ''}
                                        onValueChange={(v) =>
                                            onMap(item.key, v)
                                        }
                                        containerClassName='w-full max-w-xs'
                                        className='border-border h-8'
                                        searchable
                                        searchPlaceholder='지표 검색…'
                                        options={[
                                            {
                                                value: '',
                                                label: '(매핑 안 함)',
                                            },
                                            ...indicators.map((i) => ({
                                                value: i.fieldId,
                                                label: `${i.nameKo}${i.unit ? ` (${i.unit})` : ''}`,
                                            })),
                                        ]}
                                    />
                                    {p && p.by !== 'rule' && p.reason ? (
                                        <p className='text-muted-foreground mt-1 text-xs'>
                                            AI: {Math.round(p.confidence * 100)}
                                            % · {p.reason}
                                        </p>
                                    ) : null}
                                    {p && p.by === 'rule' ? (
                                        <Badge tone='info' className='mt-1'>
                                            규칙 매칭
                                        </Badge>
                                    ) : null}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </>
    )
}
